import React, { useMemo } from 'react';
import { FormattedMessage } from '../../../util/reactIntl';
import css from '../../OrderPanel/FeesInputFields/FeesInputFields.module.css';
import { IconCollection, Map } from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import { DELIVERY_FEE, PICKUP_FEE, USAGE_MULTIPLE } from '../../../util/constants';
import {
    EXACT_LOCATION_ZOOM,
    buildLocationMapsConfig,
    shouldShowExactLocation,
    useMapboxReady,
} from '../../Map/locationMapPresentation';

/**
 * Store Profile cart panel's pickup-location map preview - kept as a standalone
 * component (rather than folded into the shared DeliveryReturnSelectField widget) so
 * this stays a Store-Profile-only affordance for now; the listing page cart drawer has
 * no equivalent yet (see the wider cart-consistency plan for why).
 *
 * Only shown when NOT both delivery and pickup services are selected - once the item is
 * fully couriered both ways, there's no pickup location left for the renter to see.
 *
 * Mirrors StoreLocationMap.js's exact-location/privacy-circle presentation (RAS-2581) -
 * `showAddressInStore` comes from the store owner's profile (threaded down from
 * StoreProfilePage), while geolocation/address stay listing-level, matching this
 * component's existing per-item lookups below.
 */
const PickupLocationMap = ({ listing, values, deliveryPickup, showAddressInStore = false }) => {
    const config = useConfiguration();
    const isMapboxReady = useMapboxReady();
    const { geolocation, publicData } = listing?.attributes || {};
    const { location } = publicData || {};

    const {
        globalDeliveryFee,
        globalPickupFee,
        isDeliveryRequired,
        isPickupRequired,
    } = deliveryPickup || {};

    // Determine if delivery service is selected (Delivery by Owner) - CartModal structure
    const isDeliveryServiceSelected =
        isDeliveryRequired ||
        values?.global?.optionalFees?.includes(DELIVERY_FEE) ||
        (globalDeliveryFee?.feeCalculationType === USAGE_MULTIPLE &&
            typeof values?.global?.deliveryFee === 'number' && values?.global?.deliveryFee > 0);

    // Determine if pickup service is selected (Return Pick up by Owner) - CartModal structure
    const isPickupServiceSelected =
        isPickupRequired ||
        values?.global?.optionalFees?.includes(PICKUP_FEE) ||
        (globalPickupFee?.feeCalculationType === USAGE_MULTIPLE &&
            typeof values?.global?.pickupFee === 'number' && values?.global?.pickupFee > 0);

    // Hide map if BOTH delivery and pickup services are selected
    const shouldShowMap = !(isDeliveryServiceSelected && isPickupServiceSelected);

    // Same shared privacy-circle/exact-location rules as the listing page, checkout
    // page, and Store Profile sidebar map (RAS-2473/RAS-2474/RAS-2581) - an owner only
    // gets the exact-address treatment when they opted in *and* this item's address
    // actually exists.
    const showExactLocation = shouldShowExactLocation(showAddressInStore, location?.address);

    const mapMapsConfig = useMemo(() => (
        geolocation?.lat && geolocation?.lng
            ? buildLocationMapsConfig(config.maps, showExactLocation)
            : null
    ), [config.maps, geolocation?.lat, geolocation?.lng, showExactLocation]);

    // Static image first, then upgrade to the interactive map once Mapbox GL has
    // loaded so the +/- zoom controls become usable - matches the listing/checkout/
    // store-sidebar maps instead of requiring a tap.
    const useStaticMap = !isMapboxReady;

    const mapProps = useMemo(() => (
        geolocation && geolocation.lat && geolocation.lng && mapMapsConfig
            ? {
                address: location?.address,
                center: geolocation, // Used for circle center
                obfuscatedCenter: geolocation, // Required when fuzzy.enabled, using actual location for accurate circle
                mapsConfig: mapMapsConfig,
                useStaticMap,
                showFuzzyCircleInitially: !showExactLocation,
                ...(showExactLocation ? { zoom: EXACT_LOCATION_ZOOM } : {}),
            }
            : null
    ), [geolocation, useStaticMap, location?.address, mapMapsConfig, showExactLocation]);

    // Don't render if map shouldn't be shown or props are invalid
    if (!shouldShowMap || !mapProps) {
        return null;
    }

    return (
        <div onClick={e => e.stopPropagation()} className={css.mapContainer}>
            <div className={css.map}>
                <Map
                    key={`pickup-map-${geolocation.lat}-${geolocation.lng}-${useStaticMap ? 'static' : 'dynamic'}-${showExactLocation ? 'exact' : 'fuzzy'}`}
                    {...mapProps}
                />
            </div>
            {showExactLocation ? (
                <div className={`${css.locationMessage} ${css.locationMessagePickup}`}>
                    <FormattedMessage
                        id="StoreLocationMap.pickupConfirmedMessage"
                        values={{
                            addressLine: (
                                <span className={css.pickupAddressLine}>
                                    <i className="ri-map-pin-2-line" aria-hidden="true" />
                                    <strong>{location.address}</strong>
                                </span>
                            ),
                        }}
                    />
                </div>
            ) : (
                <div className={css.locationMessage}>
                    <IconCollection className={css.infoIcon} name="info" />
                    <FormattedMessage
                        id="DeliveryPickupOptionsField.exactLocationAfterBooking"
                    />
                </div>
            )}
        </div>
    );
};

export default PickupLocationMap;
